// ---------------------------------------------------------------------------
// Tribos (campista/equipe). Lê minha_tribo(), a curiosidade do dia, o detalhe
// público de cada tribo e a configuração do organizador. Nada é criado no
// banco aqui — só chamadas de leitura/gravação via RPC.
// ---------------------------------------------------------------------------

import { supabase } from './supabase';

export interface TriboItem {
  id: number;
  nome: string;
  cor: string | null;
}

export type RespostaTribo = 'tribo' | 'nao_campista';

export interface MinhaTribo {
  logado: boolean;
  equipe: boolean;
  papel: string | null;
  evento_id: number | null;
  tribo: TriboItem | null;
  resposta: RespostaTribo | null;
  precisa_perguntar: boolean;
  opcoes: TriboItem[];
  todas: TriboItem[];
}

export interface CuriosidadeTribo {
  tem: boolean;
  tribo: TriboItem | null;
  texto: string;
  ref: string;
  numero: number;
  total: number;
  vistas: number;
  nova: boolean;
  semana: string;
  proxima_em: string;
}

export interface CuriosidadeTexto {
  numero: number;
  texto: string;
  ref: string;
  desta_semana: boolean;
}

export interface TriboDetalhe {
  permitido: boolean;
  id: number;
  nome: string;
  significado: string;
  resumo: string;
  historia: string;
  reflexao: string;
  oracao: string;
  lema: string;
  minha: boolean;
  curiosidades_total: number;
  curiosidades_vistas: number;
  curiosidades: CuriosidadeTexto[];
  cor: string | null;
  todas: TriboItem[];
}

export interface TriboConfigItem {
  id: number;
  nome: string;
  usa: boolean;
  cor: string | null;
  ordem: number;
}

export interface TribosConfig {
  configurado: boolean;
  sugestao_evento_id: number | null;
  sugestao_evento_nome: string | null;
  tribos: TriboConfigItem[];
}

export interface ItemSalvarTribo {
  tribo_id: number;
  cor: string | null;
  ordem: number;
}

const VAZIO: MinhaTribo = {
  logado: false,
  equipe: false,
  papel: null,
  evento_id: null,
  tribo: null,
  resposta: null,
  precisa_perguntar: false,
  opcoes: [],
  todas: [],
};

export const TRIBO_VAZIA = VAZIO;

// Paleta de 16 cores fortes para o organizador pintar cada tribo.
export const PALETA_TRIBO = [
  '#E11D48',
  '#EA580C',
  '#F59E0B',
  '#EAB308',
  '#84CC16',
  '#22C55E',
  '#10B981',
  '#14B8A6',
  '#06B6D4',
  '#0EA5E9',
  '#6366F1',
  '#8B5CF6',
  '#A855F7',
  '#D946EF',
  '#EC4899',
  '#F43F5E',
];

// Cor neutra quando a tribo ainda não tem cor escolhida.
export const COR_TRIBO_PADRAO = '#64748B';

export function corValida(hex: string | null | undefined): string | null {
  if (!hex) return null;
  const v = hex.trim();
  return /^#[0-9a-fA-F]{6}$/.test(v) ? v.toUpperCase() : null;
}

// Converte "#RRGGBB" num rgba com a transparência pedida (fundo suave do chip).
export function corRgba(hex: string | null | undefined, alpha: number): string {
  const v = corValida(hex) ?? COR_TRIBO_PADRAO;
  const h = v.slice(1);
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function texto(v: unknown, padrao = ''): string {
  return typeof v === 'string' ? v : padrao;
}

function lerTribo(v: unknown): TriboItem | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  const id = Number(o.id);
  if (!Number.isFinite(id)) return null;
  return { id, nome: texto(o.nome), cor: corValida(texto(o.cor) || null) };
}

function lerTribos(v: unknown): TriboItem[] {
  if (!Array.isArray(v)) return [];
  return v.map((o) => lerTribo(o)).filter((t): t is TriboItem => t !== null);
}

export async function buscarMinhaTribo(): Promise<MinhaTribo> {
  const { data, error } = await supabase.rpc('minha_tribo');
  if (error) throw error;
  const d = (data ?? {}) as Record<string, unknown>;
  const resposta =
    d.resposta === 'tribo' || d.resposta === 'nao_campista'
      ? (d.resposta as RespostaTribo)
      : null;
  return {
    logado: d.logado === true,
    equipe: d.equipe === true,
    papel: typeof d.papel === 'string' ? d.papel : null,
    evento_id: Number.isFinite(Number(d.evento_id)) ? Number(d.evento_id) : null,
    tribo: lerTribo(d.tribo),
    resposta,
    precisa_perguntar: d.precisa_perguntar === true,
    opcoes: lerTribos(d.opcoes),
    todas: lerTribos(d.todas),
  };
}

export async function definirMinhaTribo(
  triboId: number | null,
  naoCampista = false,
): Promise<TriboItem | null> {
  const { data, error } = await supabase.rpc('definir_minha_tribo', {
    p_tribo_id: triboId,
    p_nao_campista: naoCampista,
  });
  if (error) throw error;
  const d = (data ?? {}) as Record<string, unknown>;
  return lerTribo(d.tribo);
}

export async function buscarCuriosidadeDaMinhaTribo(): Promise<CuriosidadeTribo> {
  const { data, error } = await supabase.rpc('curiosidade_da_minha_tribo');
  if (error) throw error;
  const d = (data ?? {}) as Record<string, unknown>;
  return {
    tem: d.tem === true,
    tribo: lerTribo(d.tribo),
    texto: texto(d.texto),
    ref: texto(d.ref),
    numero: Number(d.numero) || 0,
    total: Number(d.total) || 0,
    vistas: Number(d.vistas) || 0,
    nova: d.nova === true,
    semana: texto(d.semana),
    proxima_em: texto(d.proxima_em),
  };
}

export async function buscarTriboDetalhe(triboId: number): Promise<TriboDetalhe> {
  const { data, error } = await supabase.rpc('tribo_detalhe', { p_tribo_id: triboId });
  if (error) throw error;
  const d = (data ?? {}) as Record<string, unknown>;
  const curiosidades = Array.isArray(d.curiosidades)
    ? (d.curiosidades as unknown[])
        .map((c) => {
          const o = (c ?? {}) as Record<string, unknown>;
          return {
            numero: Number(o.numero) || 0,
            texto: texto(o.texto),
            ref: texto(o.ref),
            desta_semana: o.desta_semana === true,
          };
        })
        .filter((c) => c.texto)
    : [];
  return {
    permitido: d.permitido === true,
    id: Number(d.id) || triboId,
    nome: texto(d.nome),
    significado: texto(d.significado),
    resumo: texto(d.resumo),
    historia: texto(d.historia),
    reflexao: texto(d.reflexao),
    oracao: texto(d.oracao),
    lema: texto(d.lema),
    minha: d.minha === true,
    curiosidades_total: Number(d.curiosidades_total) || 0,
    curiosidades_vistas: Number(d.curiosidades_vistas) || 0,
    curiosidades,
    cor: corValida(texto(d.cor) || null),
    todas: lerTribos(d.todas),
  };
}

export async function buscarTribosConfigEvento(eventoId: number): Promise<TribosConfig> {
  const { data, error } = await supabase.rpc('tribos_config_evento', { p_evento: eventoId });
  if (error) throw error;
  const d = (data ?? {}) as Record<string, unknown>;
  const tribos = Array.isArray(d.tribos)
    ? (d.tribos as unknown[])
        .map((t) => {
          const o = (t ?? {}) as Record<string, unknown>;
          const id = Number(o.id);
          if (!Number.isFinite(id)) return null;
          return {
            id,
            nome: texto(o.nome),
            usa: o.usa === true,
            cor: corValida(texto(o.cor) || null),
            ordem: Number(o.ordem) || 0,
          };
        })
        .filter((t): t is TriboConfigItem => t !== null)
        .sort((a, b) => a.ordem - b.ordem)
    : [];
  return {
    configurado: d.configurado === true,
    sugestao_evento_id: Number.isFinite(Number(d.sugestao_evento_id))
      ? Number(d.sugestao_evento_id)
      : null,
    sugestao_evento_nome: typeof d.sugestao_evento_nome === 'string' ? d.sugestao_evento_nome : null,
    tribos,
  };
}

export async function salvarTribosEvento(
  eventoId: number,
  itens: ItemSalvarTribo[],
): Promise<void> {
  const { error } = await supabase.rpc('salvar_tribos_evento', {
    p_evento: eventoId,
    p_itens: itens,
  });
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Pedido "abrir a folha da tribo" (o perfil usa para trocar a resposta).
// ---------------------------------------------------------------------------
let pedidoAberto = false;
const ouvintes = new Set<() => void>();

export function abrirFolhaTribo(): void {
  pedidoAberto = true;
  ouvintes.forEach((fn) => fn());
}

export function assinarFolhaTribo(fn: () => void): () => void {
  ouvintes.add(fn);
  return () => {
    ouvintes.delete(fn);
  };
}

export function consumirPedidoFolhaTribo(): boolean {
  const v = pedidoAberto;
  pedidoAberto = false;
  return v;
}